import { ReactElement } from 'react';

import { formatPeriod, IYearMonth } from '~/utils/date/date';
import { formatMinutesAsHoursAndMinutes } from '~/utils/date/duration';
import {
  ICeArchivedEntry,
  ICeArchivedExemption,
  ICeHistoryMonthlyRecord,
} from '~/services/api/communityEngagement/types';
import { ReactComponent as DocumentListIcon } from '~/ui/assets/images/documentList.svg';

import ProgressRow from '~/ui/pages/Clients/reusable/CommunityEngagement/components/ProgressRow';
import { formatDollars } from '~/ui/pages/Clients/reusable/CommunityEngagement/helpers/formatAmount';
import shouldShowProgress from '~/ui/pages/Clients/reusable/CommunityEngagement/helpers/shouldShowProgress';
import EngagementSkeleton from './EngagementSkeleton';
import EntryList from './EntryList';
import ExemptionList from './ExemptionList';
import getArchivedBarTone from './helpers/getArchivedBarTone';

import styles from './PlanEngagement.module.scss';

interface IMonthRecordProps {
  selected: IYearMonth;
  isLoading: boolean;
  record: ICeHistoryMonthlyRecord | null;
  archivedEntries?: ICeArchivedEntry[];
  archivedExemptions?: ICeArchivedExemption[];
}

const MonthRecord = ({
  selected,
  isLoading,
  record,
  archivedEntries = [],
  archivedExemptions = [],
}: IMonthRecordProps): ReactElement => {
  if (isLoading) return <EngagementSkeleton />;
  if (!record) {
    return (
      <p role="alert" className={styles.error}>
        Community Engagement for this month couldn&apos;t be loaded.
      </p>
    );
  }

  const { entries, exemptions } = record;

  return (
    <div className={styles.content}>
      {shouldShowProgress(exemptions, entries) && (
        <>
          <div className={styles.block}>
            <div className={styles.blockHeader}>
              <h4 className={styles.blockTitle}>Monthly Progress</h4>
              <span className={styles.period}>{formatPeriod(selected)}</span>
            </div>
            <div className={styles.progressRows}>
              <ProgressRow
                label="Hours"
                total={record.totalMinutesWorked}
                threshold={record.monthlyMinutesThreshold}
                tone={getArchivedBarTone(record.monthlyMinutesThreshold)}
                format={formatMinutesAsHoursAndMinutes}
              />
              <ProgressRow
                label="Dollars"
                total={record.totalCurrencyEarned}
                threshold={record.monthlyCurrencyThreshold}
                tone={getArchivedBarTone(record.monthlyCurrencyThreshold)}
                format={formatDollars}
              />
            </div>
          </div>
          <hr className={styles.divider} />
        </>
      )}
      {(exemptions.length > 0 || archivedExemptions.length > 0) && (
        <ExemptionList exemptions={exemptions} archivedExemptions={archivedExemptions} />
      )}
      {!entries.length && !exemptions.length && (
        <div className={styles.emptyState}>
          <DocumentListIcon className={styles.emptyIcon} />
          <p className={styles.emptyText}>No entries for this month</p>
        </div>
      )}
      {(entries.length > 0 || archivedEntries.length > 0) && (
        <EntryList entries={entries} archivedEntries={archivedEntries} />
      )}
    </div>
  );
};

export default MonthRecord;
